import { staticFileUrl } from "./file";

export const MAX_ACCEPTED_IMG_WIDTH = 1024;

export const getImageDimensions = async (
  file: File,
): Promise<{ width: number; height: number; naturalWidth: number; naturalHeight: number }> => {
  return await new Promise((resolve, reject) => {
    const src = window.URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve({
        width: image.width,
        height: image.height,
        naturalWidth: image.naturalWidth,
        naturalHeight: image.naturalHeight,
      });
    };

    image.src = src;
    image.onabort = reject;
  });
};

export const resizeImage = (image: any) => {
  const ratio = image.height / image.width;

  if (image.width > MAX_ACCEPTED_IMG_WIDTH) {
    return {
      width: MAX_ACCEPTED_IMG_WIDTH,
      height: Number.parseInt((ratio * MAX_ACCEPTED_IMG_WIDTH).toFixed(), 10),
    };
  }
  return {
    width: Number.parseInt(image.width.toFixed(), 10),
    height: Number.parseInt(image.height.toFixed(), 10),
  };
};

/**
 * CDN image-optimization URL builders (spec: specs/cdn-imop.md §4.1).
 *
 * Cloud CDN can transform images at the edge when a request carries an
 * `?imop=<operations>` query parameter (Pre-GA). Every `imop` URL in the web
 * app MUST be built by this helper so that a syntax change stays a one-file
 * fix and the emitted widths stay inside the canonical variant set (§5).
 *
 * Kill switch: unless `VITE_IMAGE_OPTIMIZATION === "true"` the output is
 * byte-identical to `staticFileUrl` (spec §2).
 */
export type AvatarSize = "xxs" | "xs" | "sm" | "md" | "md-lg" | "ml" | "lg" | "xl";
export type ContentImageVariant = "thumb" | "card" | "hero";

/** Canonical 2×-DPR request widths per rendered avatar size (§4.1 tables). */
const AVATAR_REQUEST_WIDTHS: Record<AvatarSize, number> = {
  xxs: 48,
  xs: 64,
  sm: 96,
  md: 96,
  "md-lg": 128,
  ml: 160,
  lg: 224,
  xl: 224,
};

/** Canonical request widths per content variant (§4.1 tables). */
const CONTENT_REQUEST_WIDTHS: Record<ContentImageVariant, number> = {
  thumb: 640,
  card: 960,
  hero: 1280,
};

/**
 * Extensions Cloud CDN can transform. `.svg` is not transformable and `.gif`
 * would lose animation; documents must never be transformed (§3.1).
 */
const TRANSFORMABLE_IMAGE_PATTERN = /\.(jpe?g|png|webp|bmp|tiff?|avif)$/i;

const imageOptimizationEnabled = (): boolean => import.meta.env.VITE_IMAGE_OPTIMIZATION === "true";

/**
 * If `fileUrl` is an absolute URL served from our static origin
 * (`VITE_STATIC_URL`, compared protocol-insensitively — the pattern used by
 * the `RewriteLegacyStaticUrlsToCdn` migration), return its bucket-relative
 * path. Returns `null` for absolute URLs on any other origin (e.g.
 * `i.pravatar.cc`), which must never be transformed.
 */
const bucketPathFromAbsoluteUrl = (fileUrl: string): string | null => {
  const staticUrl = import.meta.env.VITE_STATIC_URL;
  if (!staticUrl) {
    return null;
  }
  try {
    const file = new URL(fileUrl);
    const base = new URL(staticUrl);
    if (file.host.toLowerCase() !== base.host.toLowerCase()) {
      return null;
    }
    const basePath = base.pathname.replace(/\/+$/, "");
    if (!file.pathname.startsWith(`${basePath}/`)) {
      return null;
    }
    return file.pathname.slice(basePath.length + 1);
  } catch {
    return null;
  }
};

/**
 * Canonical operation string, always in this exact order (§5):
 * `Resize,width=W;Compress,strength=medium;Convert,format=webp` — single dimension
 * only (aspect ratio preserved; the browser's `object-fit: cover` crops visually),
 * never `height` (distortion risk) and never `scaling=stretch`. Compress,medium
 * measured −20…−26% on JPEG re-encodes (weak measured +3…+9% larger — avoided);
 * PNG sources stay lossless webp regardless of strength.
 */
const optimizedImageUrl = (
  fileUrl: string | null | undefined,
  width: number,
): string | undefined => {
  if (!fileUrl) {
    return undefined;
  }
  if (!imageOptimizationEnabled()) {
    return staticFileUrl(fileUrl);
  }

  let bucketPath: string;
  if (fileUrl.startsWith("http")) {
    const absolutePath = bucketPathFromAbsoluteUrl(fileUrl);
    if (absolutePath === null) {
      // Absolute URL on a foreign origin — pass through untouched.
      return staticFileUrl(fileUrl);
    }
    bucketPath = absolutePath;
  } else {
    bucketPath = fileUrl;
  }

  // staticFileUrl returns undefined for relative paths when VITE_STATIC_URL
  // is unset (local proxy-less setups) — mirror that.
  const baseUrl = staticFileUrl(bucketPath);
  if (!baseUrl) {
    return undefined;
  }
  if (!TRANSFORMABLE_IMAGE_PATTERN.test(bucketPath)) {
    return baseUrl;
  }

  const operations = [`Resize,width=${width}`, "Compress,strength=medium", "Convert,format=webp"];
  return `${baseUrl}?imop=${operations.join(";")}`;
};

// Emits e.g. https://cdn.heylady.io/users/42/a.jpg?imop=Resize,width=96;Compress,strength=medium;Convert,format=webp
export const avatarUrl = (
  fileUrl: string | null | undefined,
  size: AvatarSize,
): string | undefined => optimizedImageUrl(fileUrl, AVATAR_REQUEST_WIDTHS[size]);

// Emits e.g. ...?imop=Resize,width=640;Compress,strength=medium;Convert,format=webp
export const contentImageUrl = (
  fileUrl: string | null | undefined,
  variant: ContentImageVariant,
): string | undefined => optimizedImageUrl(fileUrl, CONTENT_REQUEST_WIDTHS[variant]);
